import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import {
  FaEnvelope, FaMobileAlt, FaCheckCircle,
  FaHome, FaBolt, FaMapMarkedAlt, FaUser, FaArrowLeft, FaArrowRight,
} from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';
import axios from 'axios';
import { fireAndForgetPost } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import AgentTrustChip from '../components/AgentTrustChip';
import EnquiryModal from '../components/EnquiryModal';
import './PropertySearchLive.css';

const MOBILE_BREAKPOINT = 768;

const TRUST_POINTS = [
  { icon: <FaBolt size={14} />, label: 'Instant' },
  { icon: <FaHome size={14} />, label: 'Comparable sales' },
  { icon: <FaMapMarkedAlt size={14} />, label: 'Local insights' },
  { icon: <FaCheckCircle size={14} />, label: '100% free' },
];

// Fixed display order for fields when the agent has configured the new
// field-based config. Address is always present and always required — it's the
// core of the page — so it isn't part of the configurable fields list.
const FIELD_ORDER = ['name', 'email', 'mobile'];

/**
 * Lead-capture landing page: search an address + leave contact details →
 * receive a full property report by email and/or SMS.
 *
 * Behaviour is driven by agent_settings.property_search_config (surfaced via
 * the /agency endpoint). Two paths:
 *
 *   1) Legacy `delivery_mode` (toggle | either | both) — used when the agent
 *      has not yet saved the new field-based config. Renders exactly as before.
 *
 *   2) New `{ layout, fields }` config — renders either a single form or a
 *      stepped wizard, with per-field enable + required/optional choice.
 */
function PropertySearch() {
  const { agent_slug, agentDetails, branding } = useAgentDetails();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [sendVia, setSendVia] = useState('email');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [message, setMessage] = useState('');
  const [selectKey, setSelectKey] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [deliveredVia, setDeliveredVia] = useState('email');
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [stepIndex, setStepIndex] = useState(0);
  const [searchParams] = useSearchParams();
  // QR-code arrivals render full-screen (no nav / no bottom tab bar), so the
  // wrap must reclaim the space normally reserved for them.
  const isFullScreen = searchParams.get('campaign') === 'qr_code';

  const { accentColor, buttonTextColor, isLight } = branding;

  const psConfig = useMemo(
    () => agentDetails?.property_search_config || {},
    [agentDetails],
  );
  const hasNewConfig = Array.isArray(psConfig.fields) && psConfig.fields.length > 0;

  // Legacy delivery mode — only consulted when the new config isn't set.
  const deliveryMode = (() => {
    const m = psConfig.delivery_mode;
    return m === 'either' || m === 'both' ? m : 'toggle';
  })();

  // Normalise the new config: keep only known field keys, dedupe, default
  // required=false when unset.
  const fieldsConfig = useMemo(() => {
    if (!hasNewConfig) return [];
    const seen = new Set();
    const out = [];
    psConfig.fields.forEach((f) => {
      if (!f || !FIELD_ORDER.includes(f.key) || seen.has(f.key)) return;
      if (f.enabled === false) return;
      seen.add(f.key);
      out.push({ key: f.key, required: !!f.required });
    });
    // Preserve the FIELD_ORDER ordering for predictable UX.
    return FIELD_ORDER.map((k) => out.find((f) => f.key === k)).filter(Boolean);
  }, [hasNewConfig, psConfig]);

  const layout = hasNewConfig && psConfig.layout === 'stepped' ? 'stepped' : 'single';

  const isBranded =
    agentDetails &&
    agentDetails.landing_page_slug &&
    agentDetails.landing_page_slug !== 'home-price' &&
    agentDetails.agent_name;

  const isValidMobile = (num) => /^04\d{8}$/.test(num.replace(/\s|-/g, ''));
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((val || '').trim());

  const validateMobile = (value) => {
    if (!value) { setMobileError(''); return; }
    const cleaned = value.replace(/\s|-/g, '');
    if (cleaned && !/^[\d+]+$/.test(cleaned)) {
      setMobileError('Mobile number should only contain digits.');
    } else if (cleaned.length > 0 && !cleaned.startsWith('04')) {
      setMobileError('Australian mobile numbers start with 04.');
    } else if (cleaned.length > 10) {
      setMobileError('Mobile number must be 10 digits.');
    } else if (cleaned.length > 0 && cleaned.length < 10) {
      setMobileError('Enter 10 digits (e.g. 0412 345 678).');
    } else {
      setMobileError('');
    }
  };

  // Legacy 'either' mode: disable submit until at least one contact present.
  const eitherModeMissingContact =
    !hasNewConfig && deliveryMode === 'either'
    && !(email || '').trim() && !(mobile || '').trim();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} Property Report`;
      setEmail(agentDetails.user_email || '');
    }
  }, [agentDetails]);

  useEffect(() => {
    document.body.style.backgroundImage = null;
    document.body.style.backgroundColor = null;
  }, []);

  const loadProperties = debounce((inputValue, callback) => {
    axios.get(`${window.api_host}/property-search?q=${inputValue}`)
      .then((response) => {
        const options = response.data.map((property) => ({
          label: property.address_text,
          value: property.id,
          slug: property.slug,
        }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error loading property options:', error);
        callback([]);
      });
  }, 500);

  const sendEmailReport = (alsoSms) => {
    fireAndForgetPost(`${window.api_host}/send-property-report`, {
      agent_slug,
      email,
      name: name || undefined,
      mobile: alsoSms ? mobile : undefined,
      property_slug: selectedProperty.slug,
      property_id: selectedProperty.value,
      address_text: selectedProperty.label,
      agency_id: agentDetails.agency_id,
      agent_id: agentDetails.agent_id,
      report_type: 'property_report',
      also_sending_sms: alsoSms,
    });
  };

  const sendSmsReport = (alsoEmail) => {
    fireAndForgetPost(`${window.api_host}/send-property-sms`, {
      agent_slug,
      mobile,
      name: name || undefined,
      property_id: selectedProperty.value,
      address_text: selectedProperty.label,
      agency_id: agentDetails.agency_id,
      agent_id: agentDetails.agent_id,
      custom_domain: agentDetails.custom_domain || null,
      also_sending_email: alsoEmail,
    });
  };

  // Per-field validation used by both stepped (per-step) and single (submit)
  // paths. Returns an error string or null.
  const validateField = (key, { isRequired }) => {
    if (key === 'address') {
      if (!selectedProperty) return 'Please select a property to receive the report.';
      return null;
    }
    if (key === 'name') {
      if (isRequired && !(name || '').trim()) return 'Please enter your name.';
      return null;
    }
    if (key === 'email') {
      const v = (email || '').trim();
      if (!v) return isRequired ? 'Please enter your email address.' : null;
      if (!isValidEmail(v)) return 'Please enter a valid email address.';
      return null;
    }
    if (key === 'mobile') {
      if (!mobile) return isRequired ? 'Please enter your mobile number.' : null;
      if (!isValidMobile(mobile)) return 'Please enter a valid Australian mobile number (e.g. 0412 345 678).';
      return null;
    }
    return null;
  };

  // Steps array (stepped layout only). Address is always the final step.
  const steps = useMemo(() => {
    if (layout !== 'stepped') return [];
    return [
      ...fieldsConfig.map((f) => ({ kind: 'field', key: f.key, required: f.required })),
      { kind: 'field', key: 'address', required: true },
    ];
  }, [layout, fieldsConfig]);

  const totalSteps = steps.length;

  const handleNextStep = () => {
    const step = steps[stepIndex];
    if (!step) return;
    const err = validateField(step.key, { isRequired: step.required });
    if (err) { setMessage(err); return; }
    setMessage('');
    setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
  };

  const handleBackStep = () => {
    setMessage('');
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    setShowSuccess(false);

    let useEmail = false;
    let useSms = false;

    if (hasNewConfig) {
      // Validate every enabled field (whether single or stepped layout).
      for (const f of fieldsConfig) {
        const err = validateField(f.key, { isRequired: f.required });
        if (err) { setMessage(err); return; }
      }
      const addrErr = validateField('address', { isRequired: true });
      if (addrErr) { setMessage(addrErr); return; }

      const emailEnabled = fieldsConfig.some((f) => f.key === 'email');
      const mobileEnabled = fieldsConfig.some((f) => f.key === 'mobile');
      useEmail = emailEnabled && !!(email || '').trim() && isValidEmail(email);
      useSms = mobileEnabled && !!mobile && isValidMobile(mobile);

      if (!useEmail && !useSms) {
        setMessage('Please enter your email address or mobile number.');
        return;
      }
    } else {
      // Legacy delivery_mode path — unchanged behaviour.
      if (!selectedProperty) {
        setMessage('Please select a property to receive the report.');
        return;
      }
      if (deliveryMode === 'both') {
        useEmail = true;
        useSms = true;
      } else if (deliveryMode === 'either') {
        useEmail = !!(email || '').trim();
        useSms = !!(mobile || '').trim();
        if (!useEmail && !useSms) {
          setMessage('Please enter your email address or mobile number.');
          return;
        }
      } else {
        useSms = sendVia === 'sms';
        useEmail = !useSms;
      }

      if (useEmail) {
        if (!(email || '').trim()) { setMessage('Please enter your email address.'); return; }
        if (!isValidEmail(email)) { setMessage('Please enter a valid email address.'); return; }
      }
      if (useSms) {
        if (!mobile) { setMessage('Please enter your mobile number.'); return; }
        if (!isValidMobile(mobile)) {
          setMessage('Please enter a valid Australian mobile number (e.g. 0412 345 678).');
          return;
        }
      }
    }

    if (useEmail) sendEmailReport(useSms);
    if (useSms) sendSmsReport(useEmail);

    setIsSending(true);
    setMessage('');

    const channel = useEmail && useSms ? 'both' : (useSms ? 'sms' : 'email');
    const channelLabel = channel === 'both' ? 'email and SMS' : (channel === 'sms' ? 'SMS' : 'email');

    setTimeout(() => {
      setIsSending(false);
      setSelectKey((prevKey) => prevKey + 1);
      setSelectedProperty(null);
      setStepIndex(0);
      setDeliveredVia(channel);
      setMessage(`Your report is on its way via ${channelLabel} right now.`);
      setShowSuccess(true);
    }, 1000);
  };

  if (!agentDetails) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const selectStyles = {
    control: (p, state) => ({
      ...p,
      borderRadius: 12,
      borderColor: state.isFocused ? accentColor : '#e3e8ef',
      boxShadow: state.isFocused ? `0 0 0 4px ${accentColor}26` : 'none',
      minHeight: 48,
      cursor: 'text',
      '&:hover': { borderColor: state.isFocused ? accentColor : '#e3e8ef' },
    }),
    valueContainer: (p) => ({ ...p, padding: '2px 12px' }),
    placeholder: (p) => ({ ...p, color: '#94a3b8', textAlign: 'left', fontSize: 16 }),
    input: (p) => ({ ...p, textAlign: 'left', color: '#0f172a', fontSize: 16 }),
    singleValue: (p) => ({ ...p, textAlign: 'left', fontWeight: 600, color: '#0f172a', fontSize: 16 }),
    menu: (p) => ({
      ...p, textAlign: 'left', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(15,23,42,0.16)', marginTop: 8,
    }),
    menuPortal: (p) => ({ ...p, zIndex: 99999 }),
    option: (p, state) => ({
      ...p,
      backgroundColor: state.isFocused ? `${accentColor}12` : '#fff',
      color: '#0f172a', fontSize: '0.95rem', cursor: 'pointer',
      '&:active': { backgroundColor: `${accentColor}22` },
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    loadingIndicator: (p) => ({ ...p, color: accentColor }),
  };

  // Renders a single field input by key. Used by both layouts.
  const renderField = (key, { required, autoFocus = false } = {}) => {
    if (key === 'address') {
      return (
        <AsyncSelect
          key={selectKey}
          cacheOptions
          defaultOptions
          loadOptions={loadProperties}
          onChange={setSelectedProperty}
          value={selectedProperty}
          noOptionsMessage={() => null}
          placeholder="Search property address…"
          openMenuOnClick={false}
          menuPortalTarget={document.body}
          styles={selectStyles}
          autoFocus={autoFocus}
          aria-label="Property address"
        />
      );
    }
    if (key === 'name') {
      return (
        <input
          key="name"
          type="text"
          className="psl-field"
          placeholder={required ? 'Your name' : 'Your name (optional)'}
          value={name}
          autoFocus={autoFocus}
          onChange={(e) => setName(e.target.value)}
        />
      );
    }
    if (key === 'email') {
      return (
        <input
          key="email"
          type="email"
          className="psl-field"
          placeholder={required ? 'Your email address' : 'Your email address (optional)'}
          value={email}
          autoFocus={autoFocus}
          onChange={(e) => setEmail(e.target.value)}
        />
      );
    }
    if (key === 'mobile') {
      return (
        <React.Fragment key="mobile-wrap">
          <input
            key="mobile"
            type="tel"
            className={`psl-field ${mobileError ? 'psl-field--error' : ''}`}
            placeholder={required ? 'Mobile number (04XX XXX XXX)' : 'Mobile number (optional)'}
            value={mobile}
            autoFocus={autoFocus}
            onChange={(e) => { setMobile(e.target.value); validateMobile(e.target.value); }}
            onBlur={(e) => validateMobile(e.target.value)}
          />
          {mobileError && <div className="psl-field-error">{mobileError}</div>}
        </React.Fragment>
      );
    }
    return null;
  };

  const STEP_ICONS = {
    name: <FaUser size={12} />,
    email: <FaEnvelope size={12} />,
    mobile: <FaMobileAlt size={12} />,
    address: <FaHome size={12} />,
  };
  const STEP_TITLES = {
    name: "What's your name?",
    email: "What's your email?",
    mobile: "What's your mobile?",
    address: "Which property?",
  };

  const renderSingleLayoutFields = () => {
    if (hasNewConfig) {
      return (
        <>
          {fieldsConfig.map((f) => renderField(f.key, { required: f.required }))}
          {renderField('address', { required: true })}
        </>
      );
    }
    // Legacy delivery_mode rendering — preserved unchanged.
    if (deliveryMode === 'toggle') {
      return (
        <>
          <div className="psl-toggles" role="radiogroup" aria-label="Delivery method">
            <button
              type="button"
              role="radio"
              aria-checked={sendVia === 'email'}
              className={`psl-toggle ${sendVia === 'email' ? 'psl-toggle--active' : ''}`}
              onClick={() => setSendVia('email')}
            >
              <FaEnvelope size={13} /> Email
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={sendVia === 'sms'}
              className={`psl-toggle ${sendVia === 'sms' ? 'psl-toggle--active' : ''}`}
              onClick={() => setSendVia('sms')}
            >
              <FaMobileAlt size={13} /> SMS
            </button>
          </div>
          {sendVia === 'email' && renderField('email', { required: true })}
          {sendVia === 'sms' && renderField('mobile', { required: true })}
          {renderField('address', { required: true })}
        </>
      );
    }
    return (
      <>
        {deliveryMode === 'either' && (
          <div className="psl-card-note">
            Add your email, mobile, or both — we&rsquo;ll send your report wherever you like.
          </div>
        )}
        {renderField('email', { required: deliveryMode === 'both' })}
        {renderField('mobile', { required: deliveryMode === 'both' })}
        {renderField('address', { required: true })}
      </>
    );
  };

  const isStepped = hasNewConfig && layout === 'stepped' && totalSteps > 0;
  const currentStep = isStepped ? steps[Math.min(stepIndex, totalSteps - 1)] : null;
  const isLastStep = isStepped && stepIndex === totalSteps - 1;

  return (
    <div
      className={`psl-wrap ${isMobile ? 'psl-wrap--mobile' : 'psl-wrap--desktop'}${isFullScreen ? ' psl-wrap--fullscreen' : ''}`}
      style={{
        '--psl-accent': accentColor,
        '--psl-accent-ring': `${accentColor}26`,
        '--psl-accent-soft': `${accentColor}14`,
      }}
    >
      <div className="psl-blob-layer" aria-hidden="true">
        <div className="psl-blob psl-blob--a" style={{ backgroundColor: accentColor }} />
        <div className="psl-blob psl-blob--b" style={{ backgroundColor: accentColor }} />
      </div>

      <div className={`psl-inner ${isMobile ? 'psl-inner--mobile' : 'psl-inner--desktop'}`}>
        {!isBranded && (
          <AgentTrustChip
            agentDetails={agentDetails}
            accentColor={accentColor}
            size={isMobile ? 'sm' : 'md'}
            badgeLabel="Free property reports"
          />
        )}

        <h1 className={`psl-title ${isMobile ? 'psl-title--mobile' : 'psl-title--desktop'}`}>
          Get your free{isMobile ? ' ' : <br />}
          <span style={{ color: accentColor }}>property report</span>
        </h1>

        <p className={`psl-sub ${isMobile ? 'psl-sub--mobile' : 'psl-sub--desktop'}`}>
          Search your address and we&rsquo;ll send your full report
          (valuation, sale history, comparable local sales and local neighbourhood insights)
          instantly, the moment you hit send.
        </p>

        <Form className="psl-searchcard" onSubmit={handleSubmit}>
          <div className={`psl-card ${isMobile ? 'psl-card--mobile' : ''}`}>
            <div className="psl-card-label">
              <FaBolt size={10} /> Get your instant report
            </div>

            {isStepped ? (
              <>
                <div
                  aria-label="Progress"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    marginBottom: 14,
                  }}
                >
                  {steps.map((s, i) => (
                    <div
                      key={s.key}
                      style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i <= stepIndex ? accentColor : '#e3e8ef',
                        transition: 'background 0.2s',
                      }}
                    />
                  ))}
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#0f172a' }}>
                    <span style={{ color: accentColor }}>{STEP_ICONS[currentStep.key]}</span>
                    {STEP_TITLES[currentStep.key]}
                    {!currentStep.required && (
                      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>(optional)</span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    Step {stepIndex + 1} of {totalSteps}
                  </span>
                </div>

                {renderField(currentStep.key, { required: currentStep.required, autoFocus: true })}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  {!isLastStep ? (
                    <button
                      type="button"
                      className="psl-btn psl-btn--block"
                      onClick={handleNextStep}
                      style={{
                        backgroundColor: accentColor, color: buttonTextColor,
                        border: isLight ? '1px solid #222' : 'none',
                        boxShadow: `0 6px 18px ${accentColor}33`,
                      }}
                    >
                      {currentStep.required ? 'Next' : 'Next (skip)'} <FaArrowRight size={12} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="psl-btn psl-btn--block"
                      disabled={isSending}
                      style={{
                        backgroundColor: accentColor, color: buttonTextColor,
                        border: isLight ? '1px solid #222' : 'none',
                        boxShadow: `0 6px 18px ${accentColor}33`,
                        opacity: isSending ? 0.85 : 1,
                      }}
                    >
                      {isSending
                        ? <><Spinner animation="border" size="sm" /> Sending…</>
                        : <><FaBolt size={13} /> Send my report now</>}
                    </button>
                  )}
                  {stepIndex > 0 && (
                    <button
                      type="button"
                      className="psl-btn"
                      onClick={handleBackStep}
                      style={{
                        alignSelf: 'center', backgroundColor: 'transparent',
                        color: '#64748b', border: 'none', boxShadow: 'none',
                        padding: '4px 10px', fontSize: 13, fontWeight: 500,
                      }}
                    >
                      <FaArrowLeft size={11} /> Back
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                {renderSingleLayoutFields()}
                <button
                  type="submit"
                  className="psl-btn psl-btn--block"
                  disabled={isSending || eitherModeMissingContact}
                  title={eitherModeMissingContact ? 'Enter your email or mobile number first' : undefined}
                  style={{
                    backgroundColor: accentColor,
                    color: buttonTextColor,
                    border: isLight ? '1px solid #222' : 'none',
                    boxShadow: `0 6px 18px ${accentColor}33`,
                    opacity: (isSending || eitherModeMissingContact) ? 0.85 : 1,
                    cursor: eitherModeMissingContact ? 'not-allowed' : undefined,
                  }}
                >
                  {isSending
                    ? <><Spinner animation="border" size="sm" /> Sending…</>
                    : <><FaBolt size={13} /> Send my report now</>}
                </button>
              </>
            )}

            {message && (
              showSuccess ? (
                <div className="psl-success">
                  <div className="psl-success-icon"><FaCheckCircle size={17} /></div>
                  <div className="psl-success-title">{message}</div>
                  <div className="psl-success-note">
                    {deliveredVia === 'both'
                      ? 'Check your inbox and your phone — it should arrive within a minute.'
                      : deliveredVia === 'sms'
                        ? 'It should reach your phone within a minute.'
                        : 'Check your inbox. It should arrive within a minute.'}
                  </div>
                </div>
              ) : (
                <div className="psl-msg">{message}</div>
              )
            )}
          </div>
        </Form>

        <div className={`psl-trust ${isMobile ? 'psl-trust--mobile' : ''}`}>
          {TRUST_POINTS.map((t, i) => (
            <span className="psl-trust-item" key={i}>{t.icon}{t.label}</span>
          ))}
        </div>

        {isBranded && (
          <div className={`psl-agent ${isMobile ? 'psl-agent--mobile' : ''}`}>
            {agentDetails.agent_profile_image && (
              <img
                className="psl-agent-photo"
                src={agentDetails.agent_profile_image}
                alt={agentDetails.agent_name}
              />
            )}
            <div className="psl-agent-info">
              <div className="psl-agent-name">{agentDetails.agent_name}</div>
              <div className="psl-agent-agency">{agentDetails.agency_name_short}</div>
            </div>
            <button
              type="button"
              className="psl-agent-btn"
              onClick={() => setShowEnquiry(true)}
              style={{ color: accentColor }}
            >
              Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}
            </button>
          </div>
        )}
      </div>

      <EnquiryModal
        show={showEnquiry}
        onHide={() => setShowEnquiry(false)}
        agentDetails={agentDetails}
      />
    </div>
  );
}

export default PropertySearch;
